import {
  LockOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  PlusOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import {
  Table as ATable,
  Tag,
  Tooltip,
  Typography,
  Form,
  Select,
  Radio,
  Button,
  message,
  Input,
  Modal,
  Flex,
} from 'antd';
import colors from '../../../constants/ColourConstants';
import './Invoices.css';
import { useEffect, useState, useContext } from 'react';
import { getTransactionByInvoiceId } from '../../../services/InvoiceAPIs';
import CustomButton from '../../common/Button/Button';
import moment from 'moment';
import { monthOptions, yearOptions } from './Invoices';
import AuditLogs from '../AuditLogs/AuditLogsPopup';
import FileSize from '../../../constants/FileSize';
import {
  getPastMonthAndYear,
  handlePayoutPeriod,
} from '../../../utils/helpers/helper';
import {
  AppFactsContext,
  CountryContext,
  PermissionsContext,
} from '../../common/Navigation/Navigation';
import { PAYOUT_OVERRIDE_GENERATE } from '../../../constants/PermissionConstants';
import GeneratePayoutOverrideModal from './Modals/GeneratePayoutOverrideModal';

const payout_generated_status = [
  'Added to Payout',
  'Payout Pending',
  'Payout Failure',
  'Payout Completed',
];

const row_action_icons = ['tb-edit-icon', 'tb-generate-payout-icon'];

export default function InvoiceTable({
  data,
  loading,
  title,
  handleApplySort,
  sortField,
  selectedRowKeys,
  handleRowSelection,
  handleInvoiceStatusUpdate,
  handleGeneratePayoutOverride,
}) {
  const { month, year } = getPastMonthAndYear();
  const { Text } = Typography;
  const [editableRows, setEditableRows] = useState([]);
  const [transactionData, setTransactionData] = useState({});
  const [loadingTransactionData, setLoadingTransactionData] = useState(false);
  const [auditData, setAuditData] = useState({});
  const [selectValues, setSelectValues] = useState({});
  const [periodModel, setPeriodModel] = useState(false);
  const [invoicePeriod, setInvoicePeriod] = useState({
    month: month,
    year: year,
  });
  const [overrideModal, setOverrideModal] = useState({
    open: false,
    invoiceId: null,
  });
  const [overrideLoading, setOverrideLoading] = useState(false);
  const { countries } = useContext(CountryContext);
  const { permissions } = useContext(PermissionsContext);
  const { appFacts } = useContext(AppFactsContext);

  const canGeneratePayoutOverride = (record) =>
    permissions?.includes(PAYOUT_OVERRIDE_GENERATE) &&
    record.invoiceStatus !== 'Voided' &&
    !payout_generated_status.includes(record.invoiceStatus) &&
    !record.payoutId;

  const isGeneratePayoutLocked = Boolean(
    appFacts?.generate_payouts || appFacts?.update_invoices,
  );

  const toggleRowActionIcons = (element, display) => {
    row_action_icons.forEach((name) => {
      const icon = element.children.namedItem(name);
      if (icon) {
        icon.style.display = display;
      }
    });
  };

  const handleOverrideSubmit = (invoiceId, payload) => {
    setOverrideLoading(true);
    handleGeneratePayoutOverride(invoiceId, payload)
      .then((success) => {
        if (success) {
          setOverrideModal({ open: false, invoiceId: null });
        }
      })
      .finally(() => {
        setOverrideLoading(false);
      });
  };

  const getFlag = (country) => {
    const countryObj = countries.find((c) => c.key === country);
    return countryObj
      ? `https://flagcdn.com/w40/${countryObj?.iso?.toLowerCase()}.png`
      : '';
  };
  useEffect(() => {
    setEditableRows([]);
  }, [data]);

  const columns = [
    ATable.SELECTION_COLUMN,
    ATable.EXPAND_COLUMN,
    {
      title: 'Invoice ID',
      dataIndex: 'invoiceId',
      key: 'invoice_id',
      width: 200,
      render: (value, row) => {
        const { isLocked, striked } = row;
        return (
          <Tooltip
            title={
              <>
                {value} {isLocked && <LockOutlined />}
              </>
            }
          >
            <Typography.Text
              delete={striked}
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                color: colors.colorPrimary,
              }}
            >
              <AuditLogs
                recordId={value}
                from="invoices"
                auditData={auditData[`${value}`]}
                setInvoicesAuditData={setAuditData}
              >
                {value}
                {isLocked && <LockOutlined style={{ marginLeft: 8 }} />}
              </AuditLogs>
            </Typography.Text>
          </Tooltip>
        );
      },
      sorter: true,
      sortOrder: sortField?.field === 'ids' ? sortField?.order : null,
    },
    {
      title: 'Contributor',
      dataIndex: 'contributorEmail',
      key: 'contributor',
      width: 200,
      render: (text, record) => {
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {record.country && (
              <img
                src={getFlag(record?.country)}
                alt={record.country}
                style={{
                  width: 24,
                  height: 16,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
            )}
            <Tooltip title={`(${record.country || ' - '}) ${text}`}>
              <Typography.Text
                delete={record.striked}
                style={{
                  width: 300,
                  display: 'block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  color: record.striked
                    ? colors.colorTextTertiary
                    : colors.colorText,
                }}
              >
                {text}
              </Typography.Text>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: 'Invoice Status',
      dataIndex: 'invoiceStatus',
      key: 'invoice_status',
      width: 240,
      render: (value, record) => {
        return editableRows.includes(record.invoiceId) ? (
          <>
            <Form
              style={{ display: 'flex' }}
              onFinish={(values) => {
                if (values.status === 'Ready for Payout') {
                  if (record.invoicePeriod == null) {
                    setSelectValues({
                      invoiceId: record.invoiceId,
                      status: values.status,
                    });
                    setPeriodModel(true);
                  } else {
                    const splitDate = record.invoicePeriod.split('-');
                    const period = {
                      month: splitDate[1],
                      year: splitDate[0],
                    };
                    handleInvoiceStatusUpdate(
                      'single',
                      record.invoiceId,
                      values.status,
                      period,
                    );
                  }
                } else {
                  const splitDate = record.invoicePeriod.split('-');
                  const period = {
                    month: splitDate[1],
                    year: splitDate[0],
                  };
                  handleInvoiceStatusUpdate(
                    'single',
                    record.invoiceId,
                    values.status,
                    period,
                  );
                }
              }}
            >
              <Form.Item
                name="status"
                style={{ width: '65%', margin: 0, marginRight: 12 }}
              >
                <Select placeholder="Select status" defaultValue={value}>
                  <Radio value={'Under Further Review'}>
                    Under Further Review
                  </Radio>
                  <Radio value={'Ready for Payout'}>Ready for Payout</Radio>
                  <Radio value={'Voided'}>Voided</Radio>
                </Select>
              </Form.Item>
              <Form.Item style={{ margin: 0 }}>
                <div style={{ display: 'flex' }}>
                  <CustomButton
                    htmlType="reset"
                    type="primary"
                    variant="text"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setEditableRows([]);
                    }}
                    size="middle"
                    shape="circle"
                  />
                  <CustomButton
                    htmlType="submit"
                    type="primary"
                    variant="text"
                    icon={<CheckOutlined />}
                    size="middle"
                    shape="circle"
                  />
                </div>
              </Form.Item>
            </Form>
          </>
        ) : (
          <Tooltip title={value}>
            <Typography.Text
              onMouseEnter={(e) =>
                toggleRowActionIcons(e.currentTarget, 'inline-flex')
              }
              onMouseLeave={(e) =>
                toggleRowActionIcons(e.currentTarget, 'none')
              }
              style={{
                width: 220,
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <Tag color={record.invoiceStatusType}>{value}</Tag>
              <EditOutlined
                name="tb-edit-icon"
                style={
                  record.isLocked
                    ? {
                        pointerEvents: 'none',
                        display: 'none',
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        marginLeft: 8,
                      }
                    : {
                        cursor: 'pointer',
                        display: 'none',
                        color: colors.colorPrimary,
                        marginLeft: 8,
                      }
                }
                onClick={() => setEditableRows([record.invoiceId])}
              />
              {canGeneratePayoutOverride(record) && (
                <Tooltip
                  title={
                    isGeneratePayoutLocked
                      ? 'Generate payout with overrides is unavailable while another batch job is running'
                      : 'Generate payout with overrides'
                  }
                >
                  <DollarOutlined
                    name="tb-generate-payout-icon"
                    style={
                      isGeneratePayoutLocked
                        ? {
                            display: 'none',
                            opacity: 0.5,
                            cursor: 'not-allowed',
                            color: colors.colorTextTertiary,
                            marginLeft: 8,
                          }
                        : {
                            cursor: 'pointer',
                            display: 'none',
                            color: colors.colorPrimary,
                            marginLeft: 8,
                          }
                    }
                    onClick={() =>
                      !isGeneratePayoutLocked &&
                      setOverrideModal({
                        open: true,
                        invoiceId: record.invoiceId,
                      })
                    }
                  />
                </Tooltip>
              )}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Total (in USD)',
      dataIndex: 'invoiceGrandTotal',
      key: 'total_in_usd',
      width: 145,
      render: (value) => (
        <Text style={{ float: 'right' }}>$ {value?.toFixed(2)}</Text>
      ),
      sorter: true,
      sortOrder: sortField?.field === 'amounts' ? sortField?.order : null,
    },
    {
      title: 'Invoice Period',
      dataIndex: 'invoicePeriod',
      key: 'invoicePeriod',
      width: 160,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {value ? handlePayoutPeriod(value) : value}
          </Text>
        );
      },
    },
    // {
    //   title: 'Payout Status',
    //   dataIndex: 'payoutStatus',
    //   key: 'payout_status',
    //   width: 200,
    //   render: (value, row) => {
    //     return <Tag color={row.payoutStatusType}>{value}</Tag>;
    //   },
    // },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'created_date',
      width: 140,
      render: (value) => <>{value}</>,
      sorter: true,
      sortOrder: sortField?.field === 'createdDates' ? sortField?.order : null,
    },
    {
      title: 'Last Modified',
      dataIndex: 'updatedAt',
      key: 'last_modified',
      width: 150,
      render: (value) => <>{value}</>,
      sorter: true,
      sortOrder: sortField?.field === 'modifiedDates' ? sortField?.order : null,
    },
    // {
    //   title: 'Notes',
    //   dataIndex: 'notes',
    //   key: 'Notes',
    //   width: 254,
    //   render: (value, row) => {
    //     const { striked } = row;
    //     return (
    //       <Tooltip title={<>{value}</>}>
    //         <Typography.Text
    //           delete={striked}
    //           style={{
    //             width: 240,
    //             display: 'block',
    //             overflow: 'hidden',
    //             whiteSpace: 'nowrap',
    //             textOverflow: 'ellipsis',
    //             color: colors.colorPrimary,
    //           }}
    //         >
    //           {value}
    //         </Typography.Text>
    //       </Tooltip>
    //     );
    //   },
    // },
  ];

  const transaction_columns = [
    ATable.SELECTION_COLUMN,
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transaction_Id',
      width: 220,
      render: (value, row) => {
        return (
          <Tooltip
            title={
              <>
                {value} {<LockOutlined />}
              </>
            }
          >
            <Typography.Text
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
              <LockOutlined
                style={{
                  marginLeft: 8,
                  width: '16.5px',
                  height: '18.75px',
                  color: colors.colorIcon,
                }}
              />
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      key: 'transaction_Type',
      width: 220,
      render: (value) => <>{value}</>,
    },
    {
      title: 'Amount(in USD)',
      dataIndex: 'transactionAmount',
      key: 'transaction_Amount',
      width: 240,
      render: (value) => (
        <Text style={{ float: 'right' }}>{value?.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'transactionStatus',
      key: 'transaction_Status',
      width: 220,
      render: (value, row) => {
        return <Tag color={row.statusVarient}>{value}</Tag>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'transactionDate',
      key: 'transaction_Date',
      width: 220,
      render: (value) => <>{value}</>,
    },
    // {
    //   title: '',
    //   dataIndex: 'Action',
    //   key: 'Action',
    //   width: 60,
    //   align: 'center',
    //   render: (value) => <DeleteOutlined disabled />,
    // },
  ];
  const getStatusType = (status) => {
    return (
      {
        'Contributor Approved': 'default',
        'Pending PM Approval': 'default',
        'Under Further Review': 'default',
        Voided: 'default',

        'Ready for Finance Review': 'cyan',
        'Ready for Invoice': 'cyan',
        'Added to Invoice': 'cyan',
        'Added to Payout': 'cyan',

        'Payout Pending': 'warning',

        'Payout Failure': 'error',

        'Payout Completed': 'success',
      }[status] || 'default'
    );
  };

  const handleSubmitStaus = () => {
    handleInvoiceStatusUpdate(
      'single',
      selectValues.invoiceId,
      selectValues.status,
      invoicePeriod,
    );
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { field, order } = sorter;

    handleApplySort({ field, order });
  };
  const handleInvoicePeriodModel = () => {
    return (
      <Modal
        title="Set Invoice Period"
        open={periodModel}
        width={500}
        onCancel={() => setPeriodModel(false)}
        onClose={() => setPeriodModel(false)}
        footer={[
          <Button key="back" onClick={() => setPeriodModel(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => {
              handleSubmitStaus();
              setPeriodModel(false);
              //   setConfirm(true);
            }}
            disabled={invoicePeriod.month === '' || invoicePeriod?.year === ''}
          >
            Confirm
          </Button>,
        ]}
        centered
      >
        <div>
          <Text>
            When the status is updated to <b>Ready for Payout,</b> an Invoice
            Period must be set. Set and confirm the <b>Invoice Period</b> below.
          </Text>
        </div>
        <div style={{ margin: '14px 140px 16px 0px' }}>
          <Text>Invoice Period</Text>
          <Flex gap={8}>
            <Select
              placeholder="Month"
              style={{ flex: 1 }}
              defaultValue={invoicePeriod.month}
              options={monthOptions}
              onChange={(v) => setInvoicePeriod({ ...invoicePeriod, month: v })}
            />
            <Select
              placeholder="Year"
              style={{ flex: 1 }}
              defaultValue={invoicePeriod.year}
              options={yearOptions}
              onChange={(v) => setInvoicePeriod({ ...invoicePeriod, year: v })}
            />
          </Flex>
        </div>
      </Modal>
    );
  };
  return (
    <div style={{ margin: '24px 0' }}>
      <ATable
        title={title}
        loading={loading}
        style={{ height: '100%' }}
        columns={columns}
        dataSource={data}
        pagination={false}
        rowSelection={{
          preserveSelectedRowKeys: true,
          fixed: true,
          // getCheckboxProps: (record) => ({
          //   disabled: record.isLocked,
          // }),
          // onChange: (selectedRowKeys, selectedRecords, info) =>
          //   handleRowSelection(selectedRowKeys, selectedRecords, info),
          onChange: (selectedRowKeys, selectedRecords, info) => {
            if (selectedRowKeys.length >= FileSize.SELECT_SIZE) {
              message.warning('You can select up to 30,000 rows only.');
            }
            handleRowSelection(selectedRowKeys, selectedRecords, info);
          },
          getCheckboxProps: (record) => {
            const isSelected = selectedRowKeys.includes(record.key);
            const disable =
              !isSelected && selectedRowKeys.length >= FileSize.SELECT_SIZE;

            return {
              disabled: disable || record.isLocked,
            };
          },
        }}
        expandable={{
          showExpandColumn: false,
          expandedRowRender: (record) => {
            return (
              <div
                style={{
                  margin: '0px',
                  padding: '4px 48px',
                  background: '#0000000A',
                }}
              >
                <Text>
                  <Text
                    style={{
                      size: '18px',
                      fontWeight: 600,
                      lineHeight: '39px',
                    }}
                  >
                    Associated Transactions
                  </Text>
                </Text>
                <ATable
                  bordered={true}
                  pagination={false}
                  style={{ marginBottom: 0 }}
                  loading={loadingTransactionData}
                  columns={transaction_columns}
                  dataSource={transactionData[record.invoiceId]}
                  rowSelection={{
                    preserveSelectedRowKeys: true,
                    fixed: true,
                    onChange: (selectedRowKeys, selectedRecords, info) =>
                      handleRowSelection(
                        selectedRowKeys,
                        selectedRecords,
                        info,
                      ),
                  }}
                  scroll={{
                    x: '100%',
                    y: 200,
                  }}
                  size="middle"
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '12px',
                  }}
                >
                  <div>
                    <Button
                      variant="outlined"
                      type="primary"
                      icon={<DeleteOutlined />}
                      disabled
                      // disabled={tableLoader}
                      //onClick={() => setShowCreateInvoiceModal(true)}
                    >
                      Remove Selected
                    </Button>
                  </div>
                  <div>
                    <>
                      <Form
                        style={{ display: 'flex' }}
                        onFinish={(values) => {
                          // handleInvoiceStatusUpdate(
                          //   'single',
                          //   record.invoiceId,
                          //   values.status,
                          // );
                        }}
                      >
                        <Form.Item
                          name="transaction_Id"
                          style={{ marginRight: 12 }}
                        >
                          <Input placeholder="Transaction ID" />
                        </Form.Item>
                        <Form.Item style={{ margin: 0 }}>
                          <Button
                            htmlType="submit"
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled
                          >
                            Add Transaction
                          </Button>
                        </Form.Item>
                      </Form>
                    </>
                  </div>
                </div>
              </div>
            );
          },
          onExpand: async (expanded, record) => {
            if (expanded) {
              if (!transactionData[`${record.invoiceId}`]) {
                setLoadingTransactionData(true);
                getTransactionByInvoiceId(record.invoiceId)
                  .then((res) => {
                    let map = res.map((item) => {
                      return {
                        ...item,
                        key: item.transactionId,
                        transactionDate: item.transactionDate
                          ? moment(item.transactionDate).format('YYYY-MM-DD')
                          : 'N/A',
                        statusVarient: getStatusType(item.transactionStatus),
                      };
                    });
                    setTransactionData({
                      ...transactionData,
                      [`${record.invoiceId}`]: map,
                    });
                  })
                  .catch(() => {
                    message.error('Failed to fetch invoice data');
                  })
                  .finally(() => {
                    setLoadingTransactionData(false);
                  });
              }
            }
          },
        }}
        bordered
        scroll={{
          y: 'calc(100vh - 250px)',
        }}
        onChange={handleTableChange}
      />

      {handleInvoicePeriodModel()}
      <GeneratePayoutOverrideModal
        open={overrideModal.open}
        setOpen={(open) => setOverrideModal({ ...overrideModal, open })}
        invoiceId={overrideModal.invoiceId}
        loading={overrideLoading}
        onOk={handleOverrideSubmit}
      />
    </div>
  );
}
